"use client";

import type { Auth } from "firebase/auth";
import { onAuthStateChanged } from "firebase/auth";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

import { AuthContext, type User } from "../../lib/auth/context";

export interface AuthProviderProps {
  user: User | null;
  children: React.ReactNode;
  auth: Auth;
  signOutFunction?: () => Promise<void>;
  enableSessionValidation?: boolean;
  onInvalidSession?: () => void;
  loginPath?: string;
}

export const AuthProvider: React.FunctionComponent<AuthProviderProps> = ({
  user: serverUser,
  children,
  auth,
  signOutFunction,
  enableSessionValidation,
  onInvalidSession,
  loginPath = "/login",
}) => {
  const router = useRouter();
  const [user, setUser] = useState(serverUser);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  // Determine if we should enable session validation
  const shouldValidateSession =
    enableSessionValidation !== false &&
    (typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1"));

  useEffect(() => {
    if (shouldValidateSession) {
      const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        setIsCheckingSession(false);

        // If server says we're authenticated but Firebase Auth says we're not,
        // the session is invalid (e.g., emulator restarted)
        if (serverUser && !firebaseUser) {
          console.warn(
            "[AuthProvider] Session invalid - server user exists but Firebase Auth user is null. Forcing re-login.",
          );
          // Clear the invalid session and redirect to login
          const handleSignOut = signOutFunction || (async () => {
            await auth.signOut();
            await fetch("/api/logout");
          });

          handleSignOut()
            .then(() => {
              if (onInvalidSession) {
                onInvalidSession();
              } else {
                router.push(`${loginPath}?session=invalid`);
              }
            })
            .catch((error) => {
              console.error(
                "[AuthProvider] Error during forced logout:",
                error,
              );
              // Still redirect to login even if logout fails
              if (onInvalidSession) {
                onInvalidSession();
              } else {
                router.push(`${loginPath}?session=invalid`);
              }
            });
        } else {
          // Session is valid, update user state
          setUser(serverUser);
        }
      });

      return () => unsubscribe();
    } else {
      // In production or when validation is disabled, just use the server user
      setIsCheckingSession(false);
      setUser(serverUser);
    }
  }, [serverUser, router, auth, signOutFunction, onInvalidSession, loginPath, shouldValidateSession]);

  // Show loading state while checking session validity
  if (isCheckingSession) {
    return null; // Or a loading spinner if preferred
  }

  return (
    <AuthContext.Provider
      value={{
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

